import React, {useContext, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {FaCheck, FaChevronDown, FaChevronUp, FaCircle, FaCopy, FaEdit, FaExclamationTriangle, FaEye, FaEyeSlash, FaPlus, FaSignInAlt, FaSpinner, FaTimes, FaTrash, FaUser, FaUserCheck} from 'react-icons/fa';
import {ConnectionStatusEnum} from '../../../infrastructure/events/EventBridgeComponent';
import {AppCtx} from '../../../AppCtx';
import EditableComponent from '../../../shared/components/EditableComponent';
import {getDeviceInfo} from '../../../infrastructure/storage/localStorage';
import {SessionManager} from '../../../infrastructure/storage/SessionManager';
import useYjsDocument from '../../../shared/hooks/useYjsDocument';
import {getWorkspaceId} from '../../../shared/services/dbUtils';
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';
import useOverlayParent from '../../../shared/hooks/useOverlayParent';
import {parseSessionShareHash} from '../../../shared/services/sessionShareUtils';
import './ConnectionComponentStyles.css';

export const SessionInfo = () => {
  const {currentSessionId} = useContext(AppCtx);
  const session = SessionManager.getCurrentSession();
  return <span>{currentSessionId ? `Sesja: ${session?.name || currentSessionId.substring(0, 8)}` : 'Brak aktywnej sesji'}</span>;
};

export const DeviceNameComponent = () => {
  const {deviceName, setDeviceName} = useContext(AppCtx);
  return <span>Nazwa tego urządzenia: <EditableComponent defaultValue={getDeviceInfo().guid.substring(0, 8)} inputValue={deviceName} valueSetter={setDeviceName}/></span>;
};

export const ConnectionComponent = ({hideUserCountBadge = false}) => {
  const {connectionStatus, currentSessionId, setCurrentSessionId, deviceName, setDeviceName} = useContext(AppCtx);
  const isReadOnly = useWorkspaceReadOnly();
  const canEnterSession = !isReadOnly || process.env.REACT_APP_HOSANNA_READONLY === '1' || process.env.REACT_APP_HOSANNA_DIRECT === '1';
  const parent = useOverlayParent();
  const [needsLogin] = useState(() => canEnterSession && !currentSessionId
    && !SessionManager.getCurrentSession() && !parseSessionShareHash(window.location.hash));
  const {isConnected: yjsConnected, isSynced: yjsSynced, connectedUsers} = useYjsDocument(getWorkspaceId());
  const [open, setOpen] = useState(needsLogin);
  const [tab, setTab] = useState(needsLogin ? 'sessions' : 'me');
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [details, setDetails] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deviceDraft, setDeviceDraft] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState('');
  const [creating, setCreating] = useState(() => needsLogin && !SessionManager.getSessions().length);
  const [newSession, setNewSession] = useState({name: '', username: '', password: ''});
  const [position, setPosition] = useState(null);
  const windowRef = useRef(null), triggerRef = useRef(null), dragRef = useRef(null);
  const deviceId = getDeviceInfo().guid;
  const peers = connectedUsers.filter(user => user.id !== deviceId);
  const currentSession = sessions.find(session => session.id === currentSessionId) || SessionManager.getCurrentSession();
  const connected = connectionStatus === ConnectionStatusEnum.WS_CONNECTED && yjsConnected;
  const statusLabel = connected ? (yjsSynced ? 'Zsynchronizowany' : 'Synchronizowanie...') : 'Niepołączony';

  useEffect(() => {
    if (open) {
      SessionManager.saveCurrentSession();
      setSessions(SessionManager.getSessions());
      setExpanded(currentSessionId);
    }
  }, [open, currentSessionId]);
  useEffect(() => {
    if (!open) return undefined;
    const keyDown = event => {
      if (event.key !== 'Escape') return;
      if (editing) { setEditing(false); setMessage(''); }
      else { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, [open, editing]);
  useEffect(() => {
    const reset = () => setPosition(null);
    window.addEventListener('resize', reset);
    return () => window.removeEventListener('resize', reset);
  }, []);

  const notify = (value, isError = false) => { setMessage(value); setError(isError); };
  const selectTab = value => { setTab(value); setConfirmId(null); setEditing(false); setMessage(''); };
  const copy = async (value, key) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); window.setTimeout(() => setCopied(''), 1200); }
    catch (e) { notify('Nie udało się skopiować do schowka.', true); }
  };
  const saveDevice = () => {
    if (!deviceDraft.trim()) { notify('Wpisz nazwę urządzenia.', true); return; }
    setDeviceName(deviceDraft.trim()); setEditing(false); setMessage('');
  };
  const switchSession = session => {
    if (session.id === currentSessionId && (process.env.REACT_APP_HOSANNA_READONLY !== '1' || yjsConnected)) { notify('Ta sesja jest już aktywna.'); return; }
    notify(`Przełączanie na sesję „${session.name}”...`);
    setCurrentSessionId(session.id);
    SessionManager.switchSession(session.id);
  };
  const deleteSession = id => {
    setConfirmId(null);
    if (id === currentSessionId) { notify('Nie można usunąć aktualnej sesji. Przełącz się na inną.', true); return; }
    setSessions(SessionManager.deleteSession(id)); setExpanded(null); notify('Sesja usunięta.');
  };
  const createSession = event => {
    event.preventDefault();
    if (!canEnterSession) return;
    if (!newSession.username.trim() || !newSession.password.trim()) { notify('Podaj użytkownika i hasło.', true); return; }
    if (!currentSessionId) {
      const session = SessionManager.login(newSession.username.trim(), newSession.password.trim(), newSession.name.trim() || null);
      setCurrentSessionId(session.id);
      SessionManager.switchSession(session.id);
      return;
    }
    const id = SessionManager.createNewSession(newSession.name.trim() || null, newSession.username.trim(), newSession.password.trim());
    setSessions(SessionManager.getSessions()); setExpanded(id); setCreating(false);
    setNewSession({name: '', username: '', password: ''}); notify('Dodano sesję.');
  };
  const startDrag = event => {
    if (window.matchMedia('(max-width: 640px)').matches || event.target.closest('button')) return;
    const rect = windowRef.current.getBoundingClientRect();
    dragRef.current = {x: event.clientX - rect.left, y: event.clientY - rect.top};
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveDrag = event => {
    if (!dragRef.current) return;
    const width = windowRef.current.offsetWidth, height = windowRef.current.offsetHeight;
    setPosition({left: Math.max(0, Math.min(window.innerWidth - width, event.clientX - dragRef.current.x)),
      top: Math.max(0, Math.min(window.innerHeight - height, event.clientY - dragRef.current.y))});
  };
  const icon = connectionStatus === ConnectionStatusEnum.CHECKING_LOGIN ? <FaSpinner className="fa-spin" />
    : connected ? <FaUserCheck /> : connectionStatus === ConnectionStatusEnum.NOT_LOGGED_IN ? <FaSignInAlt /> : <FaExclamationTriangle />;

  return <>
    <button ref={triggerRef} type="button" className="session-window-trigger" onClick={() => setOpen(value => !value)}
      aria-label="Sesja i użytkownik" aria-expanded={open} title="Sesja i użytkownik">
      {icon}{!hideUserCountBadge && yjsConnected && connectedUsers.length > 0 && <span className="user-count-badge">{connectedUsers.length}</span>}
    </button>
    {open && createPortal(<>
      <div className="session-window-backdrop" onClick={() => setOpen(false)} />
      <section ref={windowRef} className="session-window" role="dialog" aria-label="Sesja i użytkownik" style={position || undefined}>
        <div className="session-window-grab" />
        <div className="session-window-header" onPointerDown={startDrag} onPointerMove={moveDrag}
          onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }}>
          <span className="session-window-grip">⠿</span><span className="session-window-heading">Sesja: <strong>{currentSession?.name || 'Brak sesji'}</strong></span>
          <span className={`session-window-dot ${connected ? '' : 'offline'}`} title={statusLabel} />
          <button type="button" className="session-window-icon" onClick={() => setOpen(false)} aria-label="Zamknij"><FaTimes /></button>
        </div>
        <div className="session-window-tabs" role="tablist" aria-label="Informacje o sesji">
          {[['me', 'Ja'], ['sessions', 'Sesje'], ['users', <>Połączeni <span className="session-window-count">{peers.length + 1}</span></>]]
            .map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => selectTab(key)}>{label}</button>)}
        </div>
        <div className="session-window-body" role="tabpanel">
          {tab === 'me' && <>
            <div className="session-window-label">Nazwa tego urządzenia</div>
            {editing ? <div className="session-window-edit"><input autoFocus aria-label="Nazwa urządzenia" value={deviceDraft}
              onChange={event => setDeviceDraft(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') saveDevice(); }} />
              <button type="button" className="session-window-icon success" onClick={saveDevice} aria-label="Zapisz"><FaCheck /></button>
              <button type="button" className="session-window-icon" onClick={() => { setEditing(false); setMessage(''); }} aria-label="Anuluj"><FaTimes /></button></div>
              : <div className="session-window-row"><span className="session-window-grow strong">{deviceName || deviceId.substring(0, 8)}</span>
                <button type="button" className="session-window-icon" onClick={() => { setDeviceDraft(deviceName || ''); setEditing(true); }} aria-label="Zmień nazwę"><FaEdit /></button></div>}
            {editing && error && message && <div className="session-window-message error" role="alert">{message}</div>}
            <div className="session-window-sep" /><div className="session-window-label">Połączenie</div>
            <div className="session-window-row"><span className={`session-window-dot ${connected ? '' : 'offline'}`} /><span className="session-window-grow">{statusLabel}</span>
              <button type="button" className="session-window-link" onClick={() => setDetails(value => !value)} aria-expanded={details}>Szczegóły {details ? <FaChevronUp /> : <FaChevronDown />}</button></div>
            {details && <div className="session-window-details"><div>Socket: {connectionStatus}</div>
              <div>Yjs: {yjsConnected ? (yjsSynced ? 'połączony (zsynchronizowany)' : 'połączony (synchronizowanie...)') : 'rozłączony'}</div></div>}
            <div className="session-window-sep" /><div className="session-window-label">Sesja</div>
            <div className="session-window-row"><span className="session-window-grow strong">{currentSession?.name || 'Brak aktywnej sesji'}</span>
              <button type="button" className="session-window-link" onClick={() => selectTab('sessions')}>Zmień</button></div>
          </>}
          {tab === 'sessions' && <>
            {!sessions.length && <div className="session-window-empty">Brak zapisanych sesji</div>}
            {sessions.map(session => {
              const current = session.id === currentSessionId, active = expanded === session.id;
              return <div key={session.id} className={`session-window-session ${current ? 'current' : ''}`}>
                <div className="session-window-session-head"><button type="button" className="session-window-session-name" onClick={() => switchSession(session)}>
                  <FaCircle className={current ? 'selected' : ''} /><span className="session-window-grow">{session.name || session.id.substring(0, 8)}</span>
                  {current && <span className="session-window-badge">aktualna</span>}</button>
                  <button type="button" className="session-window-chevron" onClick={() => { setExpanded(active ? null : session.id); setConfirmId(null); }}
                    aria-expanded={active} aria-label={`Dane logowania: ${session.name || session.id}`}>{active ? <FaChevronUp /> : <FaChevronDown />}</button></div>
                {active && <div className="session-window-credentials">
                  {!session.username && !session.password && <div className="session-window-empty">Brak zapisanych danych logowania</div>}
                  {session.username && <div className="session-window-field"><span className="key">Użytkownik</span><span className="session-window-grow">{session.username}</span>
                    <button type="button" className="session-window-icon" onClick={() => copy(session.username, `u-${session.id}`)} aria-label="Kopiuj użytkownika">{copied === `u-${session.id}` ? <FaCheck /> : <FaCopy />}</button></div>}
                  {session.password && <div className="session-window-field"><span className="key">Hasło</span><span className="session-window-grow">{revealed[session.id] ? session.password : '••••••••'}</span>
                    <button type="button" className="session-window-icon" onClick={() => setRevealed(value => ({...value, [session.id]: !value[session.id]}))} aria-label={revealed[session.id] ? 'Ukryj hasło' : 'Pokaż hasło'}>{revealed[session.id] ? <FaEyeSlash /> : <FaEye />}</button>
                    <button type="button" className="session-window-icon" onClick={() => copy(session.password, `p-${session.id}`)} aria-label="Kopiuj hasło">{copied === `p-${session.id}` ? <FaCheck /> : <FaCopy />}</button></div>}
                  <div className="session-window-actions">{confirmId === session.id ? <><span>Usunąć sesję?</span>
                    <button type="button" className="danger" onClick={() => deleteSession(session.id)}>Tak</button><button type="button" onClick={() => setConfirmId(null)}>Nie</button></>
                    : <button type="button" className="danger" onClick={() => current ? notify('Nie można usunąć aktualnej sesji. Przełącz się na inną.', true) : setConfirmId(session.id)}><FaTrash /> Usuń</button>}</div>
                </div>}</div>;
            })}
            {canEnterSession && (creating ? <form className="session-window-new" onSubmit={createSession}>
              <input aria-label="Nazwa sesji (opcjonalnie)" placeholder="Nazwa sesji (opcjonalnie)" value={newSession.name} onChange={event => setNewSession(value => ({...value, name: event.target.value}))} />
              <input autoFocus={needsLogin} autoComplete="username" aria-label="Użytkownik" placeholder="Użytkownik" value={newSession.username} onChange={event => setNewSession(value => ({...value, username: event.target.value}))} />
              <input aria-label="Hasło" type="password" placeholder="Hasło" value={newSession.password} onChange={event => setNewSession(value => ({...value, password: event.target.value}))} />
              <div className="session-window-actions"><button type="submit" className="success">{currentSessionId ? 'Utwórz' : 'Zaloguj'}</button><button type="button" onClick={() => setCreating(false)}>Anuluj</button></div>
            </form> : <button type="button" className="session-window-add" onClick={() => setCreating(true)}><FaPlus /> Nowa sesja</button>)}
          </>}
          {tab === 'users' && <><div className="session-window-row"><FaUser /><span className="session-window-grow strong">{deviceName || 'To urządzenie'}</span>
            <span className="session-window-id">{deviceId.substring(0, 8)}</span><span className="session-window-badge neutral">Ty</span></div>
            {peers.map(user => <div className="session-window-row" key={user.clientId || user.id}><FaUser /><span className="session-window-grow strong">{user.name || 'Anonimowy'}</span>
              <span className="session-window-id">{String(user.id || user.clientId).substring(0, 8)}</span></div>)}</>}
          {message && !(tab === 'me' && editing && error) && <div className={`session-window-message ${error ? 'error' : ''}`} role="status">{message}</div>}
        </div>
      </section>
    </>, parent)}
  </>;
};

export default ConnectionComponent;
